import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const HERO_IMAGE =
  "https://customer-assets.emergentagent.com/job_metamorph-saga/artifacts/oivt9a3p__L2A0180.jpg";

/**
 * `/lyrics` — Metamorph lyric sheet, full booklet experience.
 *
 * Mirrors `/tour` and `/chronology` exactly so it sits naturally alongside
 * them as a triptych: hero image with title overlay bottom-left, then the
 * credits block, then each chapter rendered with a label · title · accent
 * rule · serif lyric lines.
 *
 * Saved and routed but intentionally NOT linked anywhere in the public nav
 * yet. The artist will surface it from the site after Metamorph is
 * released; until then it's a private artefact mirrored from the PDF at
 * `/api/admin/lyrics/pdf`.
 */
export const CREDITS = [
  ["Written by", "George Maple"],
  ["Produced by", "George Maple"],
  ["Vocal Production by", "George Maple"],
  ["Vocal Engineering by", "Luke Pickering and George Maple"],
  ["Recorded at", "The Church Studios, London"],
  ["Cello and Piano performed by", "Nomzeplume"],
  ["Chapters 1–4 mixed by", "Simon Cohen at 301 Studios, Sydney"],
  ["Chapter 5 mixed by", "Tyler Scott at Clean Cut Collective, Hollywood CA"],
  ["Mastered by", "Dale Becker, Becker Mastering"],
  ["Artwork by", "Luke Dubbelde"],
  ["Released and distributed by", "George Maple"],
];

export const CHAPTERS = [
  {
    label: "Chapter 1",
    title: "Up Here",
    verses: [
      ["Speak to me softly", "Tell me no lies", "How long have I been here", "Am I still dressed in disguise"],
      ["Tell me you love me", "A thousand times", "It will never be enough"],
      ["'Cause angels", "Taught me how to", "Fly away from how I feel", "So I just stay suspended", "Up here"],
      ["Ooo", "Ooo"],
    ],
  },
  {
    label: "Chapter 2",
    title: "Aerofe",
    verses: [["Sayehooo", "Sayehoo", "Ooo"]],
  },
  {
    label: "Chapter 3",
    title: "A Name",
    verses: [
      [
        "Say my name",
        "Say it till it tires you out",
        "Say it till it wears you down",
        "Drench me with your doubt",
        "Face it all",
        "The shadows that you ignore",
        "It's time to understand",
        "The depths of your command",
      ],
      ["Mmmmm", "Ahhhh"],
    ],
  },
  {
    label: "Chapter 4",
    title: "Tiny Box",
    verses: [
      [
        "Safe",
        "Inside the tiny box",
        "That I have made",
        "And everything that hurts me",
        "Miles away",
        "Covered by the web",
        "That I create",
        "And so I hide",
        "Somewhere in between",
        "The cold and fire",
        "An empty space",
        "With no one eye to eye",
        "I will sit here waiting by the tide",
      ],
      ["Ahhh"],
    ],
  },
  {
    label: "Chapter 4a",
    title: "Gravity",
    verses: [["Haa"]],
  },
  {
    label: "Chapter 5",
    title: "Leave the Winter",
    verses: [
      [
        "I climbed out of a photograph",
        "Into a scene",
        "Tripped up on my own underworld",
        "Of memories",
        "Just when I thought that I was done",
        "It followed me",
        "Thank god — false alarm",
      ],
      [
        "Now I feel the",
        "Change of season",
        "Petal falls and",
        "Love is speaking",
        "Terra-beauty,",
        "Golden",
        "Shaped by the gods",
      ],
      [
        "Do you feel the",
        "Change of seasons",
        "Type of love that",
        "Tastes like freedom",
        "There's no way",
        "That you can fall",
        "In the stars",
      ],
      [
        "Come share it all",
        "Come share it all with me",
        "Come share it all",
        "Come share it all with me",
        "Come share it all",
        "Come share it all with me",
        "Coz I…",
      ],
      [
        "Shake it out shake it out",
        "(Leave the winter)",
        "Let it go let it go",
        "(Hear the whisper)",
        "Turn around, turn around",
        "Tell me how",
        "(Let it thaw)",
        "Ha",
      ],
      [
        "Shake it out shake it out",
        "(Leave the winter)",
        "Let it go let it go",
        "(Hear the whisper)",
        "Turn around, turn around",
        "It's time to",
        "It's time to thaw",
      ],
      [
        "Do you feel the",
        "Change of seasons",
        "Close your eyes",
        "And taste the freedom",
        "There's no way",
        "That you can fall in the stars",
      ],
      [
        "Coz I will let it go",
        "I'll get it done",
        "I will let it go",
        "I'll get it done",
        "I will let it go",
        "I'll let it float",
        "Into the wind",
        "I'll let it float into the wind",
        "I'll let it go",
      ],
      [
        "Shake it out shake it out",
        "(Leave the winter)",
        "Let it go let it go",
        "(Hear the whisper)",
        "Turn around, turn around",
        "Tell me how",
        "(Let it thaw)",
        "Ha",
      ],
      [
        "Shake it out shake it out",
        "(Leave the winter)",
        "Let it go let it go",
        "(Hear the whisper)",
        "Turn around, turn around",
        "It's time to",
        "It's time to thaw",
      ],
      [
        "Come share it all",
        "Come share it all with me",
        "Come share it all",
        "Come share it all with me",
        "Come share it all",
        "Come share it all with me",
        "Coz I…",
      ],
    ],
  },
];

export default function Lyrics() {
  // `?print=1` hides the trailing nav (store CTA, back link) so the page can
  // be screenshotted into a self-contained booklet without any site
  // navigation peeking through at the bottom.
  const [searchParams] = useSearchParams();
  const isPrint = searchParams.get("print") === "1";
  return (
    <main className="min-h-screen bg-[var(--mm-bg,#050a1a)] text-white" data-testid="lyrics-page">
      {/* Hero — same treatment as /tour and /chronology */}
      <div className="relative w-full aspect-[3/2] sm:aspect-[16/9] max-h-[70vh] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
          data-testid="lyrics-hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--mm-bg,#050a1a)] via-transparent to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-6 sm:p-10">
          <h1
            className="deco-text text-5xl sm:text-7xl uppercase leading-[0.95] mb-1"
            style={{ letterSpacing: "0.1em" }}
            data-testid="lyrics-title"
          >
            Metamorph
          </h1>
          <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-white/55 mb-3">
            By George Maple
          </p>
          <h2
            className="text-2xl sm:text-3xl mb-4"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "#f3e0a6", letterSpacing: "0.02em", fontWeight: 500 }}
          >
            Credits &middot; Lyrics
          </h2>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 sm:px-10 py-12 sm:py-16">
        {/* Credits */}
        <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55 mb-4">Credits</p>
        <dl className="mb-14 sm:mb-16" data-testid="lyrics-credits">
          {CREDITS.map(([label, value]) => (
            <div key={label} className="grid grid-cols-[200px_1fr] gap-3 py-2 border-b border-white/[0.06] last:border-b-0">
              <dt className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/50 pt-1">
                {label}
              </dt>
              <dd
                className="text-[15px] text-white/85"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
              >
                {value}
              </dd>
            </div>
          ))}
        </dl>

        {/* Lyrics */}
        <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55 mb-5">Lyrics</p>
        <div className="space-y-14 sm:space-y-16" data-testid="lyrics-chapters">
          {CHAPTERS.map((ch, i) => (
            <section key={i} data-testid={`lyrics-chapter-${i}`}>
              <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/50 mb-2">
                {ch.label}
              </p>
              <h3
                className="text-3xl sm:text-4xl mb-3 leading-[1.05]"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "#f3e0a6", letterSpacing: "0.02em", fontWeight: 500 }}
              >
                {ch.title}
              </h3>
              <div className="h-px w-10 bg-[var(--mm-accent)]/55 mb-7" />
              <div className="space-y-6">
                {ch.verses.map((verse, vi) => (
                  <div key={vi}>
                    {verse.map((line, li) => (
                      <p
                        key={li}
                        className="text-[16px] sm:text-[17px] text-white/85 leading-[1.55]"
                        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                      >
                        {line}
                      </p>
                    ))}
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>

        {/* CTA back into store — hidden in `?print=1` so the booklet
            export doesn't carry the site nav at the bottom. */}
        {!isPrint && (
          <>
            <Link
              to="/store"
              className="btn-line w-full justify-center text-sm py-3.5 mt-16 mb-6"
              data-testid="lyrics-to-store"
            >
              Browse the store <ArrowRight className="w-4 h-4" strokeWidth={1.4} />
            </Link>

            <p className="text-center mt-10 font-mono text-[10px] uppercase tracking-[0.32em] text-white/40">
              <Link to="/" className="hover:text-white/85">&larr; Back to Metamorph</Link>
            </p>
          </>
        )}
      </div>
    </main>
  );
}
